<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav extra-active="SmmNetworkDetail" />

    <div v-if="loading" class="text-center text-gray-500 py-12">{{ $t('common.loading') }}</div>

    <template v-else-if="network">
      <div class="mb-4">
        <router-link :to="{ name: 'SmmNetworks' }" class="text-sm text-gray-700 hover:underline">{{ $t('smm.network_detail.back') }}</router-link>
        <h2 class="text-xl font-semibold mt-2">{{ network.name }}</h2>
        <p class="text-sm text-gray-500">
          {{ network.slug }}
          <span v-if="network.username"> · @{{ network.username }}</span>
        </p>
        <div v-if="network.shop || network.platform" class="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            v-if="network.shop"
            class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border"
            :style="shopBadgeStyle(network.shop)"
          >
            <span
              class="w-2 h-2 rounded-full shrink-0 border border-black/10"
              :style="{ backgroundColor: network.shop.color || '#111827' }"
            />
            {{ network.shop.name }}
          </span>
          <span
            v-if="network.platform"
            class="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
          >
            {{ network.platform?.name || network.platform?.slug || network.platform }}
          </span>
        </div>
        <div v-if="network.followers_count != null" class="mt-2">
          <span class="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-400">
            {{ $t('smm.networks.followers_label', { count: Number(network.followers_count).toLocaleString($i18n.locale === 'ru' ? 'ru-RU' : 'en-US') }) }}
          </span>
        </div>
        <p v-if="network.profile_url" class="text-sm mt-1">
          <a :href="network.profile_url" target="_blank" rel="noopener" class="text-gray-700 hover:underline break-all">
            {{ network.profile_url }}
          </a>
        </p>
      </div>

      <pinterest-panel
        v-if="network.platform?.slug === 'pinterest' || network.slug === 'pinterest'"
        :network-id="network.id"
      />

      <div class="bg-white shadow-md rounded-lg mb-6">
        <div class="flex justify-between items-center p-4 border-b">
          <h3 class="font-semibold">{{ $t('smm.network_detail.prompts_title') }}</h3>
          <button @click="openPromptForm()" class="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg">
            {{ $t('smm.network_detail.add_prompt') }}
          </button>
        </div>
        <div v-if="!network.prompt_templates?.length" class="p-6 text-gray-500 text-sm text-center">
          {{ $t('smm.network_detail.prompts_empty') }}
        </div>
        <ul v-else class="divide-y">
          <li v-for="p in network.prompt_templates" :key="p.id" class="p-4">
            <div class="flex justify-between gap-4">
              <div class="min-w-0 flex-1">
                <span class="text-xs uppercase tracking-wide text-gray-400">{{ kindLabel(p.kind) }}</span>
                <h4 class="font-medium">{{ p.name }}</h4>
                <p class="text-xs text-gray-500">{{ p.slug }}</p>
                <p v-if="p.slug === 'prefix'" class="text-xs text-amber-700 mt-1">
                  {{ $t('smm.network_detail.prefix_hint') }}
                </p>
                <details v-if="p.output_schema" class="mt-2">
                  <summary class="text-xs text-gray-700 cursor-pointer">{{ $t('smm.network_detail.schema_summary') }}</summary>
                  <pre class="text-xs bg-gray-50 p-2 rounded mt-1">{{ JSON.stringify(p.output_schema, null, 2) }}</pre>
                </details>
              </div>
              <div class="flex flex-col gap-2 shrink-0">
                <button @click="openPromptForm(p)" class="text-sm text-gray-600 hover:text-gray-900">{{ $t('common.edit') }}</button>
                <button @click="removePrompt(p)" class="text-sm text-gray-600 hover:text-red-600">{{ $t('common.delete') }}</button>
                <button
                  v-if="p.kind === 'schedule' && p.slug !== 'prefix'"
                  @click="generateSchedule(p)"
                  :disabled="generating"
                  class="text-sm bg-green-600 text-white px-2 py-1 rounded disabled:opacity-50"
                >
                  {{ generating ? $t('common.generating', { label: aiProviderLabel }) : $t('smm.network_detail.generate_schedule') }}
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <ai-generations-panel
        :social-network-id="network.id"
        type="schedule"
      />
    </template>

    <!-- Prompt modal -->
    <div
      v-if="showPromptModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="showPromptModal = false"
    >
      <div class="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4">
        <h3 class="font-semibold mb-4">{{ editingPrompt ? $t('smm.network_detail.edit_prompt') : $t('smm.network_detail.new_prompt') }}</h3>
        <form @submit.prevent="savePrompt" class="space-y-3">
          <input v-model="promptForm.name" required :placeholder="$t('common.name')" class="w-full border rounded-lg p-2" />
          <input v-model="promptForm.slug" :placeholder="$t('common.slug')" class="w-full border rounded-lg p-2" />
          <select v-model="promptForm.kind" class="w-full border rounded-lg p-2">
            <option value="schedule">{{ $t('smm.prompt_kind.schedule') }}</option>
            <option value="post_image">{{ $t('smm.prompt_kind.post_image') }}</option>
            <option value="post_video">{{ $t('smm.prompt_kind.post_video') }}</option>
            <option value="custom">{{ $t('smm.prompt_kind.custom') }}</option>
          </select>
          <textarea v-model="promptForm.body" required rows="6" class="w-full border rounded-lg p-2" :placeholder="$t('smm.network_detail.prompt_body_placeholder')" />
          <div>
            <label class="text-sm text-gray-600">{{ $t('smm.network_detail.schema_label') }}</label>
            <textarea
              v-model="schemaText"
              rows="6"
              class="w-full border rounded-lg p-2 font-mono text-xs"
              :placeholder="$t('smm.network_detail.schema_placeholder')"
            />
          </div>
          <div class="flex justify-end gap-2">
            <button type="button" @click="showPromptModal = false" class="px-4 py-2">{{ $t('common.cancel') }}</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-lg">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import AiGenerationsPanel from '@/components/smm/AiGenerationsPanel.vue'
import PinterestPanel from '@/components/smm/PinterestPanel.vue'

export default {
  components: { SmmSubNav, AiGenerationsPanel, PinterestPanel },
  data: () => ({
    network: null,
    loading: true,
    generating: false,
    aiProviderLabel: 'AI',
    showPromptModal: false,
    editingPrompt: null,
    promptForm: { name: '', slug: '', kind: 'schedule', body: '', output_schema: null },
    schemaText: '',
  }),
  mounted() {
    this.loadAiSettings()
    this.load()
  },
  methods: {
    shopBadgeStyle(shop) {
      const bg = shop?.color || '#111827'
      return {
        backgroundColor: `${bg}18`,
        borderColor: bg,
        color: bg,
      }
    },
    async loadAiSettings() {
      try {
        const { data } = await axios.get('/api/v1/smm/ai-settings')
        this.aiProviderLabel = data.provider_label || 'AI'
      } catch {
        this.aiProviderLabel = 'AI'
      }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await axios.get(`/api/v1/smm/social-networks/${this.$route.params.id}`)
        this.network = data.data
      } finally {
        this.loading = false
      }
    },
    kindLabel(kind) {
      const key = kind === 'custom' ? 'custom' : kind
      const path = `smm.prompt_kind.${key}`
      return this.$te(path) ? this.$t(path) : kind
    },
    openPromptForm(p = null) {
      this.editingPrompt = p
      if (p) {
        this.promptForm = { ...p }
        this.schemaText = p.output_schema ? JSON.stringify(p.output_schema, null, 2) : ''
      } else {
        this.promptForm = { name: '', slug: '', kind: 'schedule', body: '', output_schema: null }
        this.schemaText = ''
      }
      this.showPromptModal = true
    },
    async savePrompt() {
      let output_schema = null
      if (this.schemaText.trim()) {
        try {
          output_schema = JSON.parse(this.schemaText)
        } catch {
          alert(this.$t('smm.network_detail.invalid_schema'))
          return
        }
      }
      const payload = { ...this.promptForm, output_schema }
      const id = this.$route.params.id
      if (this.editingPrompt) {
        await axios.put(`/api/v1/smm/social-networks/${id}/prompt-templates/${this.editingPrompt.id}`, payload)
      } else {
        await axios.post(`/api/v1/smm/social-networks/${id}/prompt-templates`, payload)
      }
      this.showPromptModal = false
      await this.load()
    },
    async removePrompt(p) {
      if (!confirm(this.$t('smm.network_detail.delete_prompt_confirm', { name: p.name }))) return
      await axios.delete(`/api/v1/smm/social-networks/${this.$route.params.id}/prompt-templates/${p.id}`)
      await this.load()
    },
    async generateSchedule(prompt, confirmOverwrite = false) {
      this.generating = true
      try {
        const payload = { prompt_template_id: prompt.id }
        if (confirmOverwrite) {
          payload.confirm_overwrite = true
        }
        const { data } = await axios.post(
          `/api/v1/smm/social-networks/${this.network.id}/generate-schedule`,
          payload
        )
        const days = data.data?.schedule_days?.length ?? 0
        const pins = data.data?.schedule_days?.reduce((n, d) => n + (d.content_items?.length ?? 0), 0) ?? 0
        alert(this.$t('smm.network_detail.schedule_created', { days, pins }))
        this.$router.push({ name: 'SmmSchedule', query: { network_id: this.network.id } })
      } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.requires_confirmation) {
          const existing = e.response.data.existing_schedule
          const when = existing?.generated_at
            ? new Date(existing.generated_at).toLocaleString()
            : ''
          const msg = (e.response.data.message || '')
            + (when ? `\n\n${this.$t('smm.network_detail.schedule_created_at', { when })}` : '')
            + `\n\n${this.$t('smm.network_detail.overwrite_confirm')}`
          if (confirm(msg)) {
            return this.generateSchedule(prompt, true)
          }
          return
        }
        alert(e.response?.data?.message || e.message || this.$t('smm.network_detail.generate_error'))
      } finally {
        this.generating = false
      }
    },
  },
}
</script>
